import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { TusClientProvider } from 'use-tus';

import { LayoutV3 } from '../../../app-v2/components/layout/tab-layout';
import { AppContextProvider as AppContextV2Provider } from '../../../app-v2/context/app-context';
import GlobalStyle from '../../../theme/globalStyles';
import AnalyticsContextProvider from '../../analytics';
import { Ably } from '../../components/ably';
import AuthRoute from '../../components/auth/auth-route';
import { Auth0HistoryProvider } from '../../components/auth/auth0-history-provider';
import { ErrorFallback } from '../../components/error-fallback/error-fallback';
import { FileUpload } from '../../components/file-upload';
import FileUploadContextProvider from '../../components/file-upload-modal/file-upload-context';
import { Loading } from '../../components/loading/loading';
import { Paths } from '../../constants/paths';
import AblyChannelProvider from '../../context/ably-channel-context';
import AuthorizationContextProvider from '../../context/authorization-context';
import HelpHeroContextProvider from '../../context/help-hero-context';
import TenantContextProvider, { useTenant } from '../../context/tenant-context';
import { useTheme } from '../../hooks/accounts';
import { useAuthorization } from '../../hooks/authorization';
import { ErrorPage, PageNotFound } from '../../pages/not-found/error-page';
import { Redirect } from '../../pages/routes';
import { setTokenFetcher } from '../../services/api';

import { ToastProvider } from '@controlrooms/components';

const App: React.FC = () => {
  const { isAuthenticated, isLoading, error, getAccessTokenSilently, user } = useAuthorization();
  const { tenant } = useTenant();

  const [theme] = useTheme();

  // Consolidated token fetch effect - combines permission initialization and token fetcher setup
  useEffect(() => {
    if (!isAuthenticated) return;

    const tokenFetcher = () => getAccessTokenSilently({ authorizationParams: { tenant } });

    // Set token fetcher for API calls
    setTokenFetcher(tokenFetcher);

    // Initial token fetch for application permissions
    tokenFetcher();
  }, [isAuthenticated, tenant, getAccessTokenSilently]);

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <ErrorPage>
          <p>You can’t sign in right now.</p>
          <p style={{ marginTop: '0px' }}>For assistance, please contact your administrator.</p>
        </ErrorPage>
      </ThemeProvider>
    );
  }

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Loading />
      </ThemeProvider>
    );
  }

  // Email verification check removed - handled in AuthRoute to avoid duplicate checks

  return (
    <ThemeProvider theme={theme}>
      <React.Suspense fallback={<Loading />}>
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onReset={() => {
            // reset the state of your app so the error doesn't happen again
          }}
        >
          <AnalyticsContextProvider
            isAuthenticated={isAuthenticated}
            platform={'segment'}
            userTheme={theme?.name}
            writeKey={import.meta.env.REACT_APP_SEGMENT_WRITE_KEY || ''}
            userIdentification={{
              userId: user?.sub,
              userEmail: user?.email,
              userName: user?.nickname,
              userLocale: user?.locale,
              userLastName: user?.family_name,
              userFirstName: user?.given_name,
            }}
          >
            <HelpHeroContextProvider>
              <AppContextV2Provider>
                <ToastProvider maxVisible={3}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AblyChannelProvider>
                      <GlobalStyle />
                      <Ably />
                      <FileUpload />
                      <Routes>
                        <Route index element={<Navigate to="/app/redirect" replace />} />
                        <Route path={'signup'} element={<Signup />} />
                        <Route path={'redirect'} element={<AuthRoute component={Redirect} />} />
                        <Route
                          path={'t/:tenantId/*'}
                          element={<AuthRoute component={LayoutV3} />}
                        />
                        <Route
                          path={Paths.NOT_FOUND}
                          element={
                            <ErrorPage>
                              <PageNotFound />
                            </ErrorPage>
                          }
                        />
                      </Routes>
                    </AblyChannelProvider>
                  </div>
                </ToastProvider>
              </AppContextV2Provider>
            </HelpHeroContextProvider>
          </AnalyticsContextProvider>
        </ErrorBoundary>
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      </React.Suspense>
    </ThemeProvider>
  );
};

export const CRApp: React.FC = () => {
  // Removed infinite loop: useEffect without dependency array that called resetQueries() on every render
  // React Query handles cache staleness automatically, so this reset is unnecessary

  return (
    <ToastProvider maxVisible={3}>
      <TenantContextProvider>
        <Auth0HistoryProvider>
          <AuthorizationContextProvider>
            <TusClientProvider>
              <FileUploadContextProvider>
                <App />
              </FileUploadContextProvider>
            </TusClientProvider>
          </AuthorizationContextProvider>
        </Auth0HistoryProvider>
      </TenantContextProvider>
    </ToastProvider>
  );
};

const Signup: React.FC = () => {
  const { loginWithRedirect } = useAuth0();
  loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } });
  return <></>;
};

export default CRApp;
